// src/pages/outsourcing_management/InquiryWorkflowActions.tsx
//
// Status transitions for a single inquiry, as buttons in the detail dialog.
//
// This replaces the dropdown that used to live in the table row. A row is a
// row: changing an inquiry's status is an action on the inquiry, and it now
// sits with every other action on it, the way RequisitionWorkflowActions does
// for a requisition.
//
// Moving an inquiry moves its requisition with it (EE-253): Draft -> Approved
// is the requisition's internal approval, and every stage after that syncs
// down too, so each transition here invalidates the requisition queries. The
// requisition has no approve action of its own any more - this is it.
//
// Sending the email is a button of its own here, not a flag on a save. It was
// reachable only at create time before: an inquiry that was saved as a Draft,
// or one raised automatically by a requisition submission, had no way to be sent
// from this screen at all, and the only thing that looked like sending was
// moving the status to Sent / Awaiting Response - which changes the label
// without writing to anybody.
//
// The transitions are exactly what getAllowedInquiryStatuses returns - which
// mirrors ALLOWED_TRANSITIONS in backend/common/enums/InquiryStatusEnum.py.
// Nothing here decides what is legal; the backend 409s anything that is not,
// and this only decides how the legal set is presented: one primary for the
// next stage forward, a menu for stepping back, and Close on its own.
import { FunctionComponent, useContext, useState } from "react";
import { useQueryClient } from "react-query";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import SendIcon from "@mui/icons-material/Send";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { InquiryStatusEnum, InquiryType } from "types/InquiryTypes";
import { reformatDateTime } from "util/DateUtil";
import {
  apiAddInquiryNote,
  apiGetInquiryAttachments,
  apiRejectInquiry,
  apiSendInquiry,
  apiSendPurchaseOrder,
  apiUpdateInquiry,
  InquiryAttachmentType,
} from "util/network/Inquiries";
import { requisitionKeys } from "hooks/useRequisitions";
import {
  getAllowedInquiryStatuses,
  isInquiryFullyReadyToUse,
  inquiryActionLabel,
  inquiryStatusLabel,
  isInquiryFinished,
  INQUIRY_FLOW,
} from "./constants/inquiryStatusColors";

type PropsType = {
  inquiry: InquiryType;
  onChanged: () => void;
  /**
   * Every button off, with the reason under the panel. Set while the edit
   * form is open beside this: the actions act on what is saved, and the
   * form holds what is not - sending, moving or rejecting from here would
   * use a version of the inquiry the buyer is still in the middle of
   * changing.
   */
  disabled?: boolean;
  disabledReason?: string;
  /**
   * The inquiry is to be turned into a purchase order. Fired instead of
   * opening a second dialog: the conversion happens in the inquiry dialog
   * itself, beside this panel (see InquiryDialog's convert mode).
   */
  onOrder: () => void;
  /**
   * The supplier has confirmed the purchase order we sent. Same arrangement
   * as onOrder: the figures they came back with are recorded in a form in
   * the dialog itself, not in a second dialog on top of it.
   */
  onConfirmOrder: () => void;
};

// Who a send can reach, worked out the way _send_recipients does in
// backend/controllers/ecommerce/InquiryController.py so the confirmation
// shows the addresses the email actually lands in. With a shortlist every
// candidate is offered as a tick box: ticked to start with when switched on
// for email and reachable, off when the switch is off, and not tickable at
// all without an address. What is ticked when Send is pressed is what goes
// out - the switch on the shortlist is the default, the tick is the
// decision. With no shortlist it is the settled partner alone.
type SendCandidate = {
  id: number;
  name: string;
  email: string | null;
  switchedOn: boolean;
};

const describeSend = (inquiry: InquiryType) => {
  const candidates = inquiry.partnerCandidates ?? [];
  const rows: SendCandidate[] = [];
  const shortlist = candidates.length > 0;
  if (shortlist) {
    for (const candidate of candidates) {
      rows.push({
        id: candidate.partner_id,
        name: candidate.partner_name ?? `Partner #${candidate.partner_id}`,
        email: candidate.partner_email ?? null,
        switchedOn: candidate.send_email,
      });
    }
  } else if (inquiry.partner) {
    rows.push({
      id: inquiry.partner.id,
      name: inquiry.partner.info?.company ?? `Partner #${inquiry.partner.id}`,
      email: inquiry.partner.info?.email ?? null,
      switchedOn: true,
    });
  }
  const reachable = rows.filter((row) => !!row.email);
  // Once the order is placed with one of the shortlist, a further send is
  // for that supplier - a changed date, a purchase order - not a fresh round
  // of quotes to the others. So only the chosen one starts ticked; the rest
  // are still there to tick by hand. Before that, the send-email switches
  // decide, as they always did.
  const settledWith = reachable.find((row) => row.id === inquiry.partnerId);
  return {
    shortlist,
    rows,
    reachable,
    preselected:
      shortlist && settledWith
        ? [settledWith.id]
        : reachable.filter((row) => row.switchedOn).map((row) => row.id),
  };
};

const InquiryWorkflowActions: FunctionComponent<PropsType> = ({
  inquiry,
  onChanged,
  disabled = false,
  disabledReason,
  onOrder,
  onConfirmOrder,
}) => {
  const queryClient = useQueryClient();
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [busy, setBusy] = useState<InquiryStatusEnum | null>(null);
  const [backMenu, setBackMenu] = useState<null | HTMLElement>(null);
  // Rejecting is terminal and hands the decision back to the requester, so it
  // asks first and takes a reason. The step-by-step moves do not: they are
  // reversible, and the selector this replaced confirmed every one of them,
  // which made the common case slow.
  const [confirmReject, setConfirmReject] = useState(false);
  // Moving an unsent inquiry to WIP - it has not gone out to any supplier,
  // and WIP reads as "waiting on answers", so this asks first.
  const [confirmWip, setConfirmWip] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noting, setNoting] = useState(false);
  // Sending writes to somebody outside the company, so it asks first - and
  // asks differently the second time, because a re-send is a second email
  // landing in the same inbox.
  const [confirmSend, setConfirmSend] = useState(false);
  // Who is ticked in the send confirmation. Reset from the switches every
  // time it opens, so an earlier untick does not linger into the next send.
  const [picked, setPicked] = useState<number[]>([]);
  // The part files the send would attach, fetched when the confirmation
  // opens so it lists what the server actually decides for this type, and
  // the keys of the ones the buyer unticked. Null while loading.
  const [attachments, setAttachments] = useState<
    InquiryAttachmentType[] | null
  >(null);
  const [leftOut, setLeftOut] = useState<string[]>([]);
  // The conversion to a purchase order, which is how an inquiry reaches
  const [sending, setSending] = useState(false);
  // Sending the purchase order again - after a date change, or when the
  // first one went astray. The first copy goes with the conversion.
  const [confirmPurchaseOrder, setConfirmPurchaseOrder] = useState(false);
  const [sendingPurchaseOrder, setSendingPurchaseOrder] = useState(false);

  // No requisition behind it: the buyer marks it Done (EE-253). Unknown on a
  // response from before requisitionIds existed, which is not hand-raised.
  const handRaised =
    !!inquiry.requisitionIds && inquiry.requisitionIds.length === 0;
  const allowed = getAllowedInquiryStatuses(inquiry.state, handRaised);
  const currentIndex = INQUIRY_FLOW.indexOf(inquiry.state);

  // One step forward along the flow, if there is one.
  const next = allowed.find(
    (status) =>
      status !== InquiryStatusEnum.CLOSED &&
      INQUIRY_FLOW.indexOf(status) > currentIndex
  );
  // Whether the supplier has had this inquiry already: REQUISITIONS.sent_at,
  // stamped by the three send paths and by nothing else. It used to be
  // inferred from how far the inquiry had come, which reads wrong both ways -
  // an inquiry walked forward by hand has never been sent, and one sent while
  // it was still Approved has been.
  const sentBefore = !!inquiry.sentAt;

  // Everything genuinely *behind* the current status. Filtering only CLOSED
  // and `next` was not enough once APPROVED gained its skip to PO Sent: that
  // skip is neither, so it surfaced under "Go back" as an entry that jumped
  // two stages forward.
  const behind = allowed.filter(
    (status) =>
      status !== InquiryStatusEnum.CLOSED &&
      status !== next &&
      INQUIRY_FLOW.indexOf(status) < currentIndex
  );
  // Once the RFQ has gone out the floor rises: an email cannot be unsent, so
  // the inquiry may not return to a state that claims nothing went out. It
  // can still come back to Sent / Awaiting Response, which is a true
  // description of a sent inquiry - mirrors SENT_BACKWARD_FLOOR in
  // backend/common/enums/InquiryStatusEnum.py, which re-checks it.
  const sentFloorIndex = INQUIRY_FLOW.indexOf(
    InquiryStatusEnum.AWAITING_RESPONSE
  );
  const backwards = sentBefore
    ? behind.filter((status) => INQUIRY_FLOW.indexOf(status) >= sentFloorIndex)
    : behind;
  // Standing at the floor itself, with steps back that the send took away.
  // The button stays on screen saying why, rather than disappearing.
  const backBlockedBySend = sentBefore && backwards.length === 0 && behind.length > 0;
  // Rejecting is refused past Ordered - the goods are on their way, so it
  // would be a claim about the past rather than a decision about the future.
  // Mirrors reject_inquiry, which re-checks it.
  // indexOf returns -1 for a legacy or null state, which would otherwise
  // read as "before Ordered" and offer Reject on a row the backend refuses.
  // EE-253 P2b: and refused on an Inquiry carrying lines of more than one
  // requisition - rejecting hands the decision back to one requester. The
  // buyer takes the lines out instead (they go back to Awaiting Inquiry).
  const canReject =
    !isInquiryFinished(inquiry.state) &&
    currentIndex >= 0 &&
    currentIndex <= INQUIRY_FLOW.indexOf(InquiryStatusEnum.ORDERED) &&
    (inquiry.requisitionIds?.length ?? 0) <= 1;

  const { shortlist, rows, reachable, preselected } = describeSend(inquiry);
  // The one line the tooltip carries, from the switches - the confirmation
  // lists the addresses themselves and lets them be changed.
  const sendRecipients =
    preselected.length > 1
      ? `${preselected.length} suppliers`
      : preselected.length === 1
      ? reachable.find((row) => row.id === preselected[0])?.email
      : "the suppliers you pick";
  // What actually goes out: the ticks, or the single partner without a
  // shortlist.
  const going = shortlist
    ? rows.filter((row) => picked.includes(row.id))
    : reachable;
  const isDraft = inquiry.state === InquiryStatusEnum.DRAFT;
  const isFinished = isInquiryFinished(inquiry.state);
  // From PO Sent on the RFQ is a thing of the past - the order went out as
  // a purchase order, and the inquiry cannot be sent to a supplier again.
  const orderedOrLater = currentIndex >= INQUIRY_FLOW.indexOf(InquiryStatusEnum.PO_SENT);
  // Who the purchase order goes to: the settled supplier, from PO Sent on -
  // the re-send is for the copy that already went out, so it is allowed the
  // moment the first one has, not once the supplier answers. Mirrors
  // send_purchase_order, which refuses anything else with a 409 - the button
  // appears only once it would succeed, apart from the missing address,
  // which is said on it.
  const orderedWith = rows.find((row) => row.id === inquiry.partnerId);
  const purchaseOrderPossible =
    !isFinished &&
    !!inquiry.partnerId &&
    currentIndex >= INQUIRY_FLOW.indexOf(InquiryStatusEnum.PO_SENT);
  const purchaseOrderBlockedBecause = purchaseOrderPossible
    ? orderedWith?.email
      ? undefined
      : "The chosen supplier has no email address."
    : undefined;
  // Mirrors what send_inquiry refuses, so the reason is on the button rather
  // than in a toast after the fact. Reachable if anyone on the shortlist has
  // an address, or - with no shortlist - the settled partner has one. A
  // shortlist with every supplier switched off no longer blocks: the
  // confirmation is where they get ticked back on for this send.
  // A raw-material line whose lengths no longer hold what its requisition
  // lines need - lines added after the count was chosen. Mirrors the 409
  // update_inquiry and send_inquiry answer, so the supplier is never asked
  // for too little.
  const shortLine = inquiry.items?.find((item) => item.shortMm);
  const shortBecause = shortLine
    ? `A raw-material line is ${shortLine.shortMm} mm short. Change its lengths first.`
    : undefined;
  const sendBlockedBecause = isFinished
    ? `This inquiry is ${inquiryStatusLabel(inquiry.state).toLowerCase()}.`
    : shortBecause
    ? shortBecause
    : isDraft
    ? "Approve this inquiry before sending it to the supplier."
    : reachable.length === 0
    ? "This inquiry has no supplier with an email address yet."
    : undefined;
  // Everything in the panel goes quiet together while the edit form is open.
  const locked = disabled || busy !== null;
  // Completed is the buyer's call, but only once the counts back it - mirrors
  // the 409 update_inquiry answers otherwise. Said on the button rather than
  // hidden, so it is clear what is still missing.
  const nextBlockedBecause =
    next === InquiryStatusEnum.COMPLETED && !isInquiryFullyReadyToUse(inquiry)
      ? "Every line has to be fully ready to use first."
      : next === InquiryStatusEnum.APPROVED || next === InquiryStatusEnum.AWAITING_RESPONSE
      ? shortBecause
      : undefined;

  // Which part a listed file belongs to, for inquiries with several lines.
  const partName = (itemId: number) =>
    inquiry.items?.find((item) => item.id === itemId)?.product?.name;

  // The part files listed in every confirmation that emails the supplier.
  // Fetched on open so the list is what the server decides for this type.
  const loadAttachments = () => {
    setLeftOut([]);
    setAttachments(null);
    apiGetInquiryAttachments(token, inquiry.id)
      .then(setAttachments)
      // Without the list the send still works; it just cannot leave
      // anything out. Show it as empty rather than block.
      .catch(() => setAttachments([]));
  };

  const send = () => {
    setConfirmSend(false);
    setSending(true);
    const sentTo =
      going.length > 1 ? `${going.length} suppliers` : going[0]?.email;
    apiSendInquiry(
      token,
      inquiry.id,
      shortlist ? picked : undefined,
      leftOut
    )
      .then(() => {
        addToast({
          type: "success",
          message: `Inquiry #${inquiry.id} sent to ${sentTo}`,
        });
        // The send moves the inquiry to Sent / Awaiting Response when it was
        // behind it, so the dialog has to re-read either way.
        onChanged();
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Inquiry #${inquiry.id} was not sent: ${err}`,
          keep: true,
        })
      )
      .finally(() => setSending(false));
  };

  const sendPurchaseOrder = (to: string | undefined) => {
    setConfirmPurchaseOrder(false);
    setSendingPurchaseOrder(true);
    apiSendPurchaseOrder(token, inquiry.id, leftOut)
      .then(() => {
        addToast({
          type: "success",
          message: `Purchase order for inquiry #${inquiry.id} sent${
            to ? ` to ${to}` : ""
          }`,
        });
        // Nothing moves, but the audit trail grew a line.
        onChanged();
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Purchase order for inquiry #${inquiry.id} was not sent: ${err}`,
          keep: true,
        })
      )
      .finally(() => setSendingPurchaseOrder(false));
  };

  const reject = () => {
    setRejecting(true);
    apiRejectInquiry(token, inquiry.id, rejectReason.trim())
      .then(() => {
        addToast({
          type: "success",
          message: `Inquiry #${inquiry.id} rejected`,
        });
        setConfirmReject(false);
        setRejectReason("");
        // The requisition behind it went back to Submitted server-side, and
        // it is on screen elsewhere - see the note in move().
        queryClient.invalidateQueries(requisitionKeys.all);
        onChanged();
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Inquiry #${inquiry.id} was not rejected: ${err}`,
          keep: true,
        })
      )
      .finally(() => setRejecting(false));
  };

  const addNote = () => {
    setNoting(true);
    apiAddInquiryNote(token, inquiry.id, noteText.trim())
      .then(() => {
        addToast({ type: "success", message: "Note added" });
        setNoteOpen(false);
        setNoteText("");
        // A note can carry the inquiry to WIP, which syncs the requisition.
        queryClient.invalidateQueries(requisitionKeys.all);
        onChanged();
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Note not saved: ${err}`,
          keep: true,
        })
      )
      .finally(() => setNoting(false));
  };

  const move = (to: InquiryStatusEnum) => {
    setBackMenu(null);
    setBusy(to);
    // sendEmail=false: a status change is a bookkeeping step, not a reason to
    // write to the supplier. Sending is its own action. Ordered is not
    // reached this way - that is the conversion, see convert().
    apiUpdateInquiry(token, inquiry.id, { state: to }, false)
      .then(() => {
        addToast({
          type: "success",
          message: `Inquiry #${inquiry.id} moved to ${inquiryStatusLabel(to)}`,
        });
        // The linked requisition followed this move server-side, and it is on
        // screen elsewhere - the requisition list, its detail dialog, the
        // part-level indicator. Invalidating the whole key set rather than one
        // requisition: this component knows the inquiry, not which requisition
        // the backend synced.
        queryClient.invalidateQueries(requisitionKeys.all);
        onChanged();
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Could not move inquiry #${inquiry.id}: ${err}`,
          keep: true,
        })
      )
      .finally(() => setBusy(null));
  };

  // The part files a send would attach, each with a tick. Shared by every
  // confirmation that emails the supplier: the send, the conversion, the
  // purchase order re-send. Reads the attachments state above, which
  // loadAttachments fills when one of them opens.
  const AttachmentChecklist = () => (
    <>
      {/* Untick a file to keep it out of this send only - the next send
          offers it again. The Inquiry PDF is not listed: it always goes. */}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Files attached
      </Typography>
      {attachments === null ? (
        <CircularProgress size={16} sx={{ mt: 1 }} />
      ) : attachments.length === 0 ? (
        // The Inquiry PDF is the email itself in another form, not a file
        // attached to it.
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      ) : (
        <FormGroup>
          {attachments.map((file) => (
            <FormControlLabel
              key={file.key}
              control={
                <Checkbox
                  size="small"
                  checked={!leftOut.includes(file.key)}
                  onChange={(e) =>
                    setLeftOut((current) =>
                      e.target.checked
                        ? current.filter((key) => key !== file.key)
                        : [...current, file.key]
                    )
                  }
                />
              }
              label={
                <span>
                  {file.name}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {file.type}
                    {partName(file.itemId) && ` · ${partName(file.itemId)}`}
                  </Typography>
                </span>
              }
            />
          ))}
        </FormGroup>
      )}
    </>
  );

  if (allowed.length === 0) {
    return (
      <span style={{ fontSize: "0.8125rem", color: "var(--iconGrey)" }}>
        {inquiryStatusLabel(inquiry.state)} — no further changes.
      </span>
    );
  }

  return (
    /* A vertical list of same-size buttons: the panel is a column of
       decisions, and a wrapping row made the set look arbitrary - which
       button sat beside which depended on how long its label happened to
       be. */
    <Stack direction="column" spacing={1} sx={{ alignItems: "stretch" }}>
      {disabled && disabledReason && (
        <Typography variant="caption" color="text.secondary">
          {disabledReason}
        </Typography>
      )}
      {next && (
        <Tooltip title={nextBlockedBecause ?? ""}>
          <span>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="small"
              disabled={locked || !!nextBlockedBecause}
              startIcon={
                busy === next ? (
                  <CircularProgress size={14} />
                ) : (
                  <CheckCircleOutlineIcon />
                )
              }
              onClick={() => {
                // Neither half of placing an order is a plain status save: PO
                // Sent converts the inquiry and emails the order, Ordered records
                // what the supplier came back with. Both open a form in the
                // dialog itself (see InquiryDialog's order modes).
                if (next === InquiryStatusEnum.PO_SENT) {
                  onOrder();
                  return;
                }
                if (next === InquiryStatusEnum.ORDERED) {
                  onConfirmOrder();
                  return;
                }
                // Moving an Approved inquiry to WIP before it has been sent asks
                // first - WIP means "waiting on supplier answers", and one that
                // has not gone out has no answers coming.
                if (
                  next === InquiryStatusEnum.AWAITING_RESPONSE &&
                  !sentBefore
                ) {
                  setConfirmWip(true);
                  return;
                }
                move(next);
              }}
            >
              {inquiryActionLabel(next)}
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Sending is its own thing, so it gets its own line: the buttons beside
          it move the inquiry through the flow, this one writes to a supplier.
          The label carries whether that has happened before - a first send and
          a re-send are different acts, and the second one arrives in an inbox
          that already has the first. */}
      {/* A Draft has no send button at all, rather than a disabled one: it
          cannot be sent until it is approved, and offering the control
          implies otherwise. The other blocked cases (no supplier email, a
          finished inquiry) keep the disabled button and its tooltip, because
          those are fixable where the button is. */}
      {!isDraft && !orderedOrLater && (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.25rem",
          width: "100%",
        }}
      >
        {/* A disabled Button swallows its own hover, so the tooltip needs a
            wrapper that is still hoverable to hang off. */}
        <Tooltip
          title={
            sendBlockedBecause ??
            (sentBefore
              ? `Sends this inquiry to ${sendRecipients} again`
              : `Sends this inquiry to ${sendRecipients}`)
          }
        >
          <span>
            <Button
              fullWidth
              variant="contained"
              size="small"
              disabled={sending || locked || !!sendBlockedBecause}
              startIcon={
                sending ? <CircularProgress size={14} /> : <SendIcon />
              }
              onClick={() => {
                setPicked(preselected);
                loadAttachments();
                setConfirmSend(true);
              }}
            >
              {sentBefore ? "Send again" : "Send to supplier"}
            </Button>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          color={
            sendBlockedBecause || !sentBefore
              ? "text.secondary"
              : "success.main"
          }
        >
          {sendBlockedBecause ??
            (inquiry.sentAt
              ? `Sent ${reformatDateTime(inquiry.sentAt)}`
              : "Not sent to the supplier yet")}
        </Typography>
      </div>
      )}

      {/* Sits with the send, not with the status moves: like the send it
          writes to a supplier, unlike the send it goes to exactly one - the
          one the order went to - and it does not move anything. A re-send:
          the first purchase order went out with the conversion. */}
      {purchaseOrderPossible && (
        <Tooltip
          title={
            purchaseOrderBlockedBecause ??
            `Emails the purchase order to ${orderedWith?.email}`
          }
        >
          <span>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              disabled={
                sendingPurchaseOrder || locked || !!purchaseOrderBlockedBecause
              }
              startIcon={
                sendingPurchaseOrder ? (
                  <CircularProgress size={14} />
                ) : (
                  <ReceiptLongIcon />
                )
              }
              onClick={() => {
                loadAttachments();
                setConfirmPurchaseOrder(true);
              }}
            >
              Send purchase order again
            </Button>
          </span>
        </Tooltip>
      )}

      {(backwards.length > 0 || backBlockedBySend) && (
        <>
          {/* Disabled rather than hidden once the RFQ has gone out:
              update_inquiry refuses a backward move after sent_at is stamped,
              and the button used to offer one anyway - the buyer found out by
              being handed a 409. Disabled with the reason on it says the same
              thing before the click. Follows the shortlist rows, which explain
              a refused removal the same way. */}
          <Tooltip
            title={
              backBlockedBySend
                ? "Already sent to a supplier - this can no longer be moved back"
                : ""
            }
          >
            <span>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="inherit"
                disabled={locked || backBlockedBySend}
                startIcon={<UndoIcon />}
                onClick={(e) => setBackMenu(e.currentTarget)}
              >
                Go back
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={backMenu}
            open={Boolean(backMenu)}
            onClose={() => setBackMenu(null)}
          >
            {backwards.map((status) => (
              <MenuItem key={status} onClick={() => move(status)}>
                {inquiryStatusLabel(status)}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Records something that happened outside the flow - a chase by
          phone, an agreement that moves nothing - and carries the inquiry to
          WIP if it is not there yet. Available wherever the inquiry is live,
          including WIP itself. Reads "Note" because that is what it adds;
          "Other" said nothing about what would happen on click. */}
      <Button
        fullWidth
        variant="outlined"
        size="small"
        color="inherit"
        disabled={locked || noting}
        startIcon={noting ? <CircularProgress size={14} /> : <NoteAddIcon />}
        onClick={() => setNoteOpen(true)}
      >
        Note
      </Button>

      {canReject && (
        <Button
          fullWidth
          variant="outlined"
          color="error"
          size="small"
          disabled={locked || rejecting}
          startIcon={
            rejecting ? <CircularProgress size={14} /> : <BlockIcon />
          }
          onClick={() => setConfirmReject(true)}
        >
          Reject
        </Button>
      )}


      {/* Moving to WIP before anything went out is a judgement call - the
          inquiry claims "waiting on answers" but no supplier has been asked
          yet - so it asks before it moves. */}
      <Dialog
        open={confirmWip}
        onClose={() => setConfirmWip(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Move to WIP before sending?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Inquiry has not been sent to any supplier yet. Are you sure you
            want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWip(false)}>Not yet</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmWip(false);
              move(InquiryStatusEnum.AWAITING_RESPONSE);
            }}
          >
            Move to WIP
          </Button>
        </DialogActions>
      </Dialog>
      {/* The addresses themselves, not a count: this is the last look before
          an email leaves the company, and "3 suppliers" does not say which
          three. With a shortlist each name is a tick box, set from the
          shortlist's switches and changeable here for this one send - the
          final say is at the moment of sending, not in a form filled in
          earlier. A supplier without an address cannot be ticked. No body
          text beyond the list: a re-send putting a second email in the inbox
          is already said by the Sent timestamp beside the button, and one
          title serves both the first send and the next. */}
      <Dialog
        open={confirmSend}
        onClose={() => setConfirmSend(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {shortlist ? "Send to which suppliers?" : "Send to this supplier?"}
        </DialogTitle>
        <DialogContent>
          {shortlist ? (
            <FormGroup>
              {rows.map((row) => (
                <FormControlLabel
                  key={row.id}
                  disabled={!row.email}
                  control={
                    <Checkbox
                      size="small"
                      checked={picked.includes(row.id)}
                      onChange={(e) =>
                        setPicked((current) =>
                          e.target.checked
                            ? [...current, row.id]
                            : current.filter((id) => id !== row.id)
                        )
                      }
                    />
                  }
                  label={
                    <span>
                      {row.name} —{" "}
                      {row.email ? (
                        <strong>{row.email}</strong>
                      ) : (
                        <em>no email address</em>
                      )}
                      {row.email && !row.switchedOn && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Set not to be emailed on the shortlist
                        </Typography>
                      )}
                    </span>
                  }
                />
              ))}
            </FormGroup>
          ) : (
            <DialogContentText component="div">
              <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {reachable.map((row) => (
                  <li key={row.id}>
                    {row.name} — <strong>{row.email}</strong>
                  </li>
                ))}
              </ul>
            </DialogContentText>
          )}
          <AttachmentChecklist />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Not now</Button>
          <Button
            variant="contained"
            onClick={send}
            disabled={going.length === 0}
          >
            {going.length > 1 ? `Send to ${going.length}` : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Same last look as the send: the address, not a count. */}
      <Dialog
        open={confirmPurchaseOrder}
        onClose={() => setConfirmPurchaseOrder(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send the purchase order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Purchase order #{inquiry.id} goes to {orderedWith?.name} —{" "}
            <strong>{orderedWith?.email}</strong> again, with the same parts
            as the inquiry.
          </DialogContentText>
          <AttachmentChecklist />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPurchaseOrder(false)}>
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={() => sendPurchaseOrder(orderedWith?.email ?? undefined)}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject inquiry #{inquiry.id}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The inquiry is rejected and its requisition goes back to the
            requester to correct and resubmit. The reason is recorded on both.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReject(false)}>Keep it open</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejecting}
            onClick={reject}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add a note to inquiry #{inquiry.id}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Anything that happened outside the flow — a call, an agreement, a
            chase. It goes on the audit trail, and moves the inquiry to WIP if
            it is not there already.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Note (required)"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!noteText.trim() || noting}
            onClick={addNote}
          >
            Save note
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};


export default InquiryWorkflowActions;
